import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';

// Imports estáticos dos configs (CRA não suporta import dinâmico de JSON arbitrário sem code-splitting).
// Para adicionar nova empresa: criar pasta src/data/empresas/<slug>/config.json e incluir aqui.
import configKzero from '../data/empresas/kzero-consultoria/config.json';
import configKzeroSystem from '../data/empresas/kzerosystem/config.json';
import configADC from '../data/empresas/adc-innovation/config.json';
import configLousi from '../data/empresas/lousi-veller/config.json';

// Registro central de empresas disponíveis. Ordem = ordem no seletor.
const EMPRESAS_REGISTRO = [configKzero, configKzeroSystem, configADC, configLousi];

// Super admin (mesmo email usado no login do KzeroSystem e na Kzero) — vê todas as empresas.
const SUPER_ADMIN_EMAIL = 'consultoriakzero@gmail.com';

const LS_KEY = 'kzerodiary_empresa_ativa';
const SLUG_DEFAULT = 'kzero-consultoria';

const EmpresaContext = createContext(null);

/**
 * Decide se a empresa é visível para o usuário logado.
 *
 * Regras (ordem de precedência):
 *  1. Super admin (consultoriakzero@gmail.com) vê TUDO sempre.
 *  2. Tipo "cliente": `ownerEmail` + e-mails listados em `visivelPara` (delegação à contadora etc.).
 *  3. Tipo "propria" COM `visivelPara` preenchido: só quem está na lista (ou ownerEmail).
 *  4. Tipo "propria" SEM `visivelPara`: todos os usuários logados veem.
 */
export function empresaVisivelPara(empresa, emailUsuario) {
  if (!empresa) return false;
  const email = (emailUsuario || '').trim().toLowerCase();
  if (!email) return false;
  if (email === SUPER_ADMIN_EMAIL) return true;

  const owner = (empresa.ownerEmail || '').trim().toLowerCase();
  const visList = Array.isArray(empresa.visivelPara)
    ? empresa.visivelPara.map(e => (e || '').trim().toLowerCase()).filter(Boolean)
    : [];

  if (empresa.tipo === 'cliente') {
    return email === owner || visList.includes(email);
  }
  // propria (default)
  if (visList.length > 0) {
    return email === owner || visList.includes(email);
  }
  return true; // própria sem restrição explícita = visível para todos
}

export function EmpresaProvider({ children, emailUsuario }) {
  const empresasTodas = useMemo(() => EMPRESAS_REGISTRO, []);

  // Lista filtrada pelo email do usuário logado. Se emailUsuario ausente/null,
  // retornamos a lista completa — caso de carregamento de sessão (evita flicker).
  const empresas = useMemo(() => {
    if (!emailUsuario) return empresasTodas;
    return empresasTodas.filter(e => empresaVisivelPara(e, emailUsuario));
  }, [empresasTodas, emailUsuario]);

  const slugValido = useCallback((slug) => empresas.some(e => e.slug === slug), [empresas]);

  const [empresaAtivaSlug, setEmpresaAtivaSlug] = useState(() => {
    try {
      const salvo = localStorage.getItem(LS_KEY);
      if (salvo && EMPRESAS_REGISTRO.some(e => e.slug === salvo)) return salvo;
    } catch { /* localStorage indisponível */ }
    return SLUG_DEFAULT;
  });

  // Se a empresa ativa sair da lista visível (ex: usuário não-admin e empresa restrita),
  // fallback para a primeira empresa visível.
  useEffect(() => {
    if (!empresas.length) return;
    if (!slugValido(empresaAtivaSlug)) {
      setEmpresaAtivaSlug(empresas[0].slug);
    }
  }, [empresas, empresaAtivaSlug, slugValido]);

  // Persiste mudanças no localStorage
  useEffect(() => {
    try { localStorage.setItem(LS_KEY, empresaAtivaSlug); }
    catch { /* silencia quota / modo privado */ }
  }, [empresaAtivaSlug]);

  const setEmpresaAtiva = useCallback((slugOuConfig) => {
    const slug = typeof slugOuConfig === 'string' ? slugOuConfig : (slugOuConfig && slugOuConfig.slug);
    if (!slug) return;
    if (!slugValido(slug)) {
      console.warn(`[EmpresaContext] Slug desconhecido ou não visível: ${slug}. Ignorado.`);
      return;
    }
    setEmpresaAtivaSlug(slug);
  }, [slugValido]);

  const empresaAtiva = useMemo(
    () => empresas.find(e => e.slug === empresaAtivaSlug) || empresas[0] || empresasTodas[0],
    [empresas, empresasTodas, empresaAtivaSlug]
  );

  const isSuperAdmin = useMemo(() => {
    return (emailUsuario || '').trim().toLowerCase() === SUPER_ADMIN_EMAIL;
  }, [emailUsuario]);

  const value = useMemo(
    () => ({
      empresas,
      empresasTodas,
      empresaAtiva,
      setEmpresaAtiva,
      emailUsuario,
      isSuperAdmin,
      SUPER_ADMIN_EMAIL,
    }),
    [empresas, empresasTodas, empresaAtiva, setEmpresaAtiva, emailUsuario, isSuperAdmin],
  );

  return (
    <EmpresaContext.Provider value={value}>
      {children}
    </EmpresaContext.Provider>
  );
}

export function useEmpresa() {
  const ctx = useContext(EmpresaContext);
  if (!ctx) {
    throw new Error('useEmpresa deve ser chamado dentro de um <EmpresaProvider>.');
  }
  return ctx;
}

export default EmpresaContext;
